import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { getPhotoUrl } from '../utils/bucketUrl';

const Workshop = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'service1',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      titleKey: 'workshop.service1Title',
      descKey: 'workshop.service1Desc',
      items: ['workshop.service1Items1', 'workshop.service1Items2', 'workshop.service1Items3', 'workshop.service1Items4', 'workshop.service1Items5'],
      image: 'IMG_4185.jpeg'
    },
    {
      id: 'service2',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      ),
      titleKey: 'workshop.service2Title',
      descKey: 'workshop.service2Desc',
      image: 'IMG_7471.jpeg'
    },
    {
      id: 'service3',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      titleKey: 'workshop.service3Title',
      descKey: 'workshop.service3Desc',
      image: 'IMG_9961.jpeg'
    },
    {
      id: 'service4',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      titleKey: 'workshop.service4Title',
      descKey: 'workshop.service4Desc',
      image: 'IMG_0626.jpeg'
    },
  ];

  return (
    <>
      <SEO
        title={t('workshop.pageTitle')}
        description={t('workshop.pageDescription')}
        keywords={t('seo.workshop.keywords')}
        type="website"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-racing-black via-racing-darkGray to-gray-900 text-white py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
              {t('workshop.title')} <span className="text-racing-red">Meller Racing</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              {t('workshop.subtitle')}
            </p>
            
            <div className="max-w-4xl">
              <blockquote className="relative">
                <div className="absolute -top-4 -left-4 text-6xl text-racing-red/20 font-serif">"</div>
                <p className="text-xl text-gray-200 italic leading-relaxed px-8">
                  {t('workshop.facilitiesNote')}
                </p>
                <div className="absolute -bottom-4 -right-4 text-6xl text-racing-red/20 font-serif">"</div>
                <footer className="mt-6">
                  <cite className="text-racing-red font-bold not-italic">~ Meller Racing</cite>
                </footer>
              </blockquote>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="px-6 py-2 bg-racing-red/10 text-racing-red font-bold rounded-full text-sm tracking-wide uppercase mb-4 inline-block">
              {t('workshop.facilitiesBadge')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-racing-black mb-6">
              {t('workshop.facilitiesTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('workshop.facilitiesDesc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12">
            {[1, 2, 3, 4].map((num) => (
              <motion.div
                key={num}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: num * 0.1 }}
                className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-racing-red/30 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-racing-red to-racing-darkRed rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg mx-auto">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-800 font-semibold text-lg leading-relaxed">
                  {t(`workshop.facility${num}`)}
                </p>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* Services Grid with Image Backgrounds */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="px-6 py-2 bg-racing-red/10 text-racing-red font-bold rounded-full text-sm tracking-wide uppercase mb-4 inline-block">
              {t('workshop.servicesBadge')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-racing-black mb-6">
              {t('workshop.servicesTitle')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-racing-red/20 transition-all duration-500"
              >
                <div className="absolute inset-0">
                  <img
                    src={getPhotoUrl(service.image)}
                    alt={t(service.titleKey)}
                    className="w-full h-full object-cover object-center group-hover:scale-110 group-hover:rotate-1 transition-all duration-700"
                    style={service.image === 'IMG_4185.jpeg' ? { objectFit: 'cover', objectPosition: 'center 80%' } : {}}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-racing-black via-racing-black/85 to-racing-black/50 group-hover:from-racing-black/95 group-hover:via-racing-black/90 transition-all duration-500"></div>
                </div>

                <div className="relative z-10 p-10 min-h-[500px] flex flex-col justify-end">
                  <div className="transform group-hover:scale-110 transition-transform duration-300 mb-6">
                    <div className="inline-flex p-4 bg-racing-red/90 backdrop-blur-sm rounded-2xl shadow-lg">
                      <div className="text-white">
                        {service.icon}
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-display font-bold text-white mb-5 group-hover:text-racing-red transition-colors duration-300">
                    {t(service.titleKey)}
                  </h3>
                  
                  <p className="text-gray-200 text-lg leading-relaxed mb-6 group-hover:text-white transition-colors duration-300">
                    {t(service.descKey)}
                  </p>
                  
                  {service.items && (
                    <ul className="space-y-3 bg-racing-black/30 backdrop-blur-sm p-6 rounded-xl">
                      {service.items.map((itemKey, idx) => (
                        <li key={idx} className="flex items-start text-gray-300 group-hover:text-gray-100 transition-colors duration-300">
                          <svg className="w-6 h-6 text-racing-red mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">{t(itemKey)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Motorsport Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={getPhotoUrl('IMG_3941.jpeg')}
            alt="Motorsport"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-racing-black/85"></div>
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6 text-center">
              {t('workshop.motorsportTitle')}
            </h2>
            <p className="text-xl text-gray-200 mb-8 text-center leading-relaxed">
              {t('workshop.motorsportDesc')}
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((num) => (
                <motion.div
                  key={num}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: num * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20"
                >
                  <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold text-xl">{num}</span>
                  </div>
                  <p className="text-gray-200 text-center">
                    {t(`workshop.motorsport${num}`)}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 bg-racing-red/90 backdrop-blur-sm p-6 rounded-lg">
              <p className="text-white text-lg text-center leading-relaxed">
                {t('workshop.motorsportNote')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="px-6 py-2 bg-racing-red/10 text-racing-red font-bold rounded-full text-sm tracking-wide uppercase mb-4 inline-block">
              {t('workshop.processBadge')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-racing-black mb-4">
              {t('workshop.howWeWorkTitle')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((num) => (
              <motion.div
                key={num}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: num * 0.1 }}
                className="relative group"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-racing-red/30 h-full">
                  <div className="relative mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-racing-red to-racing-darkRed rounded-2xl flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold text-3xl">{num}</span>
                    </div>
                    {num < 4 && (
                      <div className="hidden lg:block absolute top-10 -right-12 w-24 h-0.5 bg-gradient-to-r from-racing-red to-transparent"></div>
                    )}
                  </div>
                  <p className="text-gray-800 text-center font-semibold text-lg leading-relaxed">
                    {t(`workshop.howWeWork${num}`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* For Whom */}
      <section className="py-20 bg-gradient-to-br from-racing-black via-racing-darkGray to-gray-900 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              {t('workshop.forWhomTitle')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((num) => (
              <motion.div
                key={num}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: num * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
              >
                <div className="w-12 h-12 bg-racing-red rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-200 text-center">
                  {t(`workshop.forWhom${num}`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Workshop;
